import _ from "lodash";
import axios from "axios";

import IMeta from "models/IMeta";
import { setLoading } from "../app/actions";
import generateQuery from "helpers/query";
import { ROLE_IDS, CUSTOM_SETTINGS } from "constants/.";
import { getOptions } from "helpers/common";

export const actionTypes = {
  GET_INVOICES_REQUEST: "GET_INVOICES_REQUEST",
  GET_INVOICES_SUCCESS: "GET_INVOICES_SUCCESS",
  GET_INVOICES_FAILURE: "GET_INVOICES_FAILURE",
  GET_INVOICE_REQUEST: "GET_INVOICE_REQUEST",
  GET_INVOICE_SUCCESS: "GET_INVOICE_SUCCESS",
  GET_INVOICE_LINES_REQUEST: "GET_INVOICE_LINES_REQUEST",
  GET_INVOICE_LINES_SUCCESS: "GET_INVOICE_LINES_SUCCESS",
  GET_INVOICE_DELIVERIES_REQUEST: "GET_INVOICE_DELIVERIES_REQUEST",
  GET_INVOICE_DELIVERIES_SUCCESS: "GET_INVOICE_DELIVERIES_SUCCESS",
  GET_INVOICE_SELECT_OPTIONS_REQUEST: "GET_INVOICE_SELECT_OPTIONS_REQUEST",
  GET_INVOICE_SELECT_OPTIONS_SUCCESS: "GET_INVOICE_SELECT_OPTIONS_SUCCESS",
};

export default class Actions {
  public static getInvoices(meta: IMeta = {}) {
    return async (dispatch, getState) => {
      dispatch(request());
      dispatch(setLoading(true));

      if (_.isEmpty(meta)) {
        meta = _.get(getState(), "invoices.meta", {});
      }

      if (!meta.pageSize) {
        const savedSettings = JSON.parse(
          localStorage.getItem("customSettings") || "{}"
        );
        _.set(
          meta,
          "pageSize",
          _.get(savedSettings, `${CUSTOM_SETTINGS.invoices}.pageSize`, 10)
        );
      }

      const url = `Invoices?$expand=CounterParty,Order
        &$select=Id,DocumentId,Number,PurchaseType,InvoiceDate,PaymentDate,
        CounterParty/Number,CounterParty/Name,InvoiceTypePortal,InvoiceAmountIncVat,
        PaidAmount,RemainingAmount,Order/Number,Read
        ${generateQuery(meta, "CustomerId")}`;

      try {
        const { data } = await axios.get(url);
        const payload = _.get(data, "value", []);
        const newMeta = {
          ...meta,
          count: +_.get(data, "odata.count", 0),
        };

        dispatch(success(payload, newMeta));
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.GET_INVOICES_REQUEST };
    }
    function success(payload, meta) {
      return { type: actionTypes.GET_INVOICES_SUCCESS, payload, meta };
    }
    function failure(error) {
      return { type: actionTypes.GET_INVOICES_FAILURE, error };
    }
  }

  public static getInvoice(id) {
    return async (dispatch) => {
      dispatch(request());
      dispatch(setLoading(true));

      const url = `Invoices?$filter=Id eq ${id}&$expand=CounterParty/PrimaryAddress,CounterParty/PrimaryAddress/Country,Order&
        $select=Number,DocumentId,InvoiceDate,PurchaseType,InCoverScheme,CounterParty/Number,CounterParty/Name,
        CounterParty/PrimaryAddress/Address1,CounterParty/PrimaryAddress/ZipCode,CounterParty/PrimaryAddress/City,
        CounterParty/PrimaryAddress/Country/ISOCode,CounterParty/OfficeNumber,CounterParty/FaxNumber,
        CounterParty/MailAddress,InvoiceTypePortal,Order/Number,PaymentDate,DeliveryDate,InvoiceAmountIncVat,
        CommissionAmount,Commission,AdvancePayment,PaidAmount,RemainingAmount,DerivativeOrder`;

      try {
        const {
          data: {
            value: [invoice],
          },
        } = await axios.get(url);
        dispatch(success(invoice));
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(failure(error));
        dispatch(setLoading(false));
      }
    };

    function request() {
      return { type: actionTypes.GET_INVOICE_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_INVOICE_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_INVOICES_FAILURE, error };
    }
  }

  public static getInvoiceLines(id, roleId) {
    return async (dispatch) => {
      dispatch(request());

      const url = `InvoiceLines?$filter=InvoiceId eq ${id}
        &$expand=Invoice,QuantityUnit,PriceUnit,Size,Shipment
        &$select=Invoice/Number,Number,Quantity,QuantityUnit/Description,Description,GrowerClaims,Size/Description,
        Price,PriceUnit/Description,LineAmountExVat,VatPercentage,ExternalNotaNumber,Shipment/DeliveryId,ShipmentId
        &$orderby=Number asc`;

      try {
        const {
          data: { value },
        } = await axios.get(url);
        const payload = _.map(value, (item) => ({ ...item, RoleId: roleId }));

        dispatch(success(payload));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_INVOICE_LINES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_INVOICE_LINES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_INVOICES_FAILURE, error };
    }
  }

  public static getInvoiceDeliveries(id, roleId) {
    return async (dispatch, getState) => {
      dispatch(request());
      const customerId = getState().app.currentCustomer.Id;
      const url = `ShipmentsExtended?$expand=OtherRelation,Lines/OrderLine/Order,Lines/Item,Supplier,Seller
        &$select=Id,DocumentId,DeliveryId,RoleId,Date,DeliveryDate,OtherRelation/Name,OtherRelation/Number,
        Seller/Name,Seller/Number,Seller/FytosanitairNumber,Supplier/Name,Supplier/Number,Bio
        &$filter=RelationId eq ${customerId} and Lines/any(line:line/Invoices/any(invoice:invoice/InvoiceId eq ${id}))
        and RoleId eq '${ROLE_IDS[roleId]}' and IsFinal
        &$orderby=Date desc,DeliveryId desc`;

      try {
        const {
          data: { value },
        } = await axios.get(url);
        dispatch(success(value));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_INVOICE_DELIVERIES_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_INVOICE_DELIVERIES_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_INVOICES_FAILURE, error };
    }
  }

  public static async getExportData(searchString) {
    const url = `Invoices?$expand=Order,CounterParty,Supplier,OriginalCustomer,Lines,Lines/QuantityUnit,
      Lines/PriceUnit,InvoiceType,Lines/Item/ProductType/ProductGroup/ProductCategory,VATPostingGroupCounterParty
      &$select=Number,InvoiceDate,PurchaseType,CounterParty/Number,CounterParty/Name,
      InvoiceType/Description,Order/Number,PaymentDate,DeliveryDate,InvoiceAmountExVat,
      InvoiceAmountIncVat,InvoiceAmountIncVat159,InvoiceAmountIncVat111,AdvancePayment,
      PaidAmount,RemainingAmount,ExternalReferenceNumber,VATPostingGroupCounterParty/Description,
      Supplier/Number,Supplier/Name,OriginalCustomer/Number,OriginalCustomer/Name,
      Lines/Number,Lines/Quantity,Lines/QuantityUnit/Description,Lines/Description,
      Lines/GrowerClaims,Lines/LineAmountExVat,Lines/LineAmountIncVat,Lines/VatPercentage,
      Lines/VatAmount,Lines/LineAmountIncVat159,Lines/LineAmountIncVat111,Lines/ProvisionPercentage,
      Lines/ProvisionAmountExVat,Lines/ProvisionVatPercentage,Lines/ProvisionVatAmount,
      Lines/ProvisionAmountExVat159,Lines/ProvisionAmountExvat111,Lines/ExternalRemarks,
      Lines/GoodsOrderNumber,Lines/Item/ProductType/ProductGroup/ProductCategory/Code
      ${generateQuery({ searchString, findAll: true }, "CustomerId")}
      &$orderby=Number asc`;

    try {
      const {
        data: { value },
      } = await axios.get(url);
      return value;
    } catch (error) {
      return error;
    }
  }

  public static getSelectOptions() {
    return async (dispatch) => {
      dispatch(request());

      try {
        const { data } = await axios.get("Invoices/Enums");
        const extraTypes = [
          "GreenAuction",
          "MaterialAuction",
          "CNBInterest",
          "Remainder",
        ];
        const typeOptions = getOptions(data, "InvoiceTypePortal").filter(
          (t) => extraTypes.indexOf(t.value) === -1
        );
        const purchaseTypes = getOptions(data, "PurchaseType");
        dispatch(success({ typeOptions, purchaseTypes }));
      } catch (error) {
        dispatch(failure(error));
      }
    };

    function request() {
      return { type: actionTypes.GET_INVOICE_SELECT_OPTIONS_REQUEST };
    }
    function success(payload) {
      return { type: actionTypes.GET_INVOICE_SELECT_OPTIONS_SUCCESS, payload };
    }
    function failure(error) {
      return { type: actionTypes.GET_INVOICES_FAILURE, error };
    }
  }
}
